import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import studioSectionPath from '../../utils/studioPaths';

const REDUCED_MOTION =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Ease-out-quart count-up for one number. Reduced motion lands instantly.
const useCountUp = (target, duration = 900) => {
  const [value, setValue] = useState(REDUCED_MOTION ? target : 0);
  const raf = useRef(null);

  useEffect(() => {
    if (REDUCED_MOTION) {
      setValue(target);
      return undefined;
    }
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 4);
      setValue(Math.round(target * eased));
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);

  return value;
};

const Metric = ({ label, value, prefix = "", accent }) => {
  const shown = useCountUp(value);
  return (
    <Box style={{ minWidth: 0 }}>
      <Box style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: accent,
            flexShrink: 0,
            boxShadow: `0 0 8px ${accent}66`,
          }}
        />
        <Typography
          style={{
            fontSize: 11.5,
            letterSpacing: 0.3,
            color: "rgba(255,255,255,0.55)",
            fontFamily: "'Nunito', 'Varela Round', sans-serif",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        style={{
          fontSize: 26,
          fontWeight: 700,
          color: "#ffffff",
          fontFamily: "'Nunito', 'Varela Round', sans-serif",
          lineHeight: 1.3,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {prefix}
        {shown.toLocaleString()}
      </Typography>
    </Box>
  );
};

// The owner's morning glance: four headline numbers (same definitions as the
// full Business overview) on a dark card that routes there. Renders nothing
// until the pulse endpoint answers, and never for non-owners (403 -> null).
const BusinessPulse = () => {
  const [pulse, setPulse] = useState(null);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    fetch("/business_pulse", { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then(setPulse)
      .catch(() => {});
  }, []);

  if (!pulse) return null;

  const open = () =>
    (window.location.href = studioSectionPath(pulse.studio_id, "overview"));

  return (
    <Box
      className="juice-in"
      role="button"
      tabIndex={0}
      aria-label="Open your business overview"
      onClick={open}
      onKeyDown={(e) => e.key === "Enter" && open()}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
        borderRadius: 18,
        padding: "20px 22px",
        margin: "18px 0",
        background:
          "linear-gradient(120deg, #0f1115 0%, #171a26 55%, #1d2b5e 130%)",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: hover
          ? "0 14px 34px rgba(15, 23, 42, 0.35)"
          : "0 6px 18px rgba(15, 23, 42, 0.22)",
        transform: hover && !REDUCED_MOTION ? "translateY(-2px)" : "none",
        transition:
          "transform .25s cubic-bezier(.22,1,.36,1), box-shadow .25s cubic-bezier(.22,1,.36,1)",
        outline: "none",
      }}
    >
      {/* one soft light sweep on entry */}
      {!REDUCED_MOTION && (
        <span
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            width: "45%",
            transform: "skewX(-18deg)",
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)",
            animation: "bp-sweep 1.6s ease-out .3s 1 both",
            pointerEvents: "none",
          }}
        />
      )}
      <style>{`@keyframes bp-sweep { from { left: -50%; } to { left: 110%; } }`}</style>

      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 14,
          gap: 12,
        }}
      >
        <Box>
          <Typography
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: "#fff",
              fontFamily: "'Nunito', 'Varela Round', sans-serif",
            }}
          >
            Business pulse
          </Typography>
          <Typography
            style={{
              fontSize: 12.5,
              color: "rgba(255,255,255,0.5)",
              fontFamily: "'Nunito', 'Varela Round', sans-serif",
            }}
          >
            Last 30 days · tap for the full overview
          </Typography>
        </Box>
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 34,
            height: 34,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.09)",
            transform: hover && !REDUCED_MOTION ? "translateX(3px)" : "none",
            transition: "transform .25s cubic-bezier(.22,1,.36,1)",
            flexShrink: 0,
          }}
        >
          <ArrowForwardIcon style={{ color: "#fff", fontSize: 18 }} />
        </Box>
      </Box>

      <Box
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
          gap: "14px 18px",
        }}
      >
        <Metric
          label="Revenue"
          value={Math.round((pulse.revenue_30d_cents || 0) / 100)}
          prefix="$"
          accent="#34d399"
        />
        <Metric label="Lessons completed" value={pulse.lessons_30d || 0} accent="#60a5fa" />
        <Metric label="Active students" value={pulse.active_students || 0} accent="#f472b6" />
        <Metric label="Pipeline contacts" value={pulse.active_leads || 0} accent="#fbbf24" />
      </Box>
    </Box>
  );
};

export default BusinessPulse;
